import React from 'react';
import { useTheme } from '@/hooks/useTheme';
import { getStatusLabel } from '@/utils/referralStatus';

export type ReferralStatus =
  | 'pending'
  | 'claimed'
  | 'scheduled'
  | 'no_show'
  | 'awaiting_mealplan'
  | 'expired'
  | 'cancelled'
  | 'complete';

export interface ReferralStatusBadgeProps {
  status: ReferralStatus | string;
  className?: string;
}

/**
 * ReferralStatusBadge Component
 * Displays referral status with appropriate styling and colors
 *
 * Status meanings:
 * - pending: Referral created, waiting to be claimed
 * - claimed: Referral claimed by patient
 * - scheduled: Patient has scheduled an appointment with the dietitian
 * - no_show: Patient did not attend the scheduled appointment
 * - awaiting_mealplan: Appointment completed but dietitian hasn't provided meal plan yet
 * - expired: Referral has expired
 * - cancelled: Referral cancelled by provider
 * - complete: Referral process completed with meal plan provided
 */
export const ReferralStatusBadge: React.FC<ReferralStatusBadgeProps> = ({
  status,
  className = ''
}) => {
  const { isDark } = useTheme();

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        // Yellow/amber for waiting states
        return isDark ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-600';

      case 'claimed':
        // Blue for claimed/in-progress states
        return isDark ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-600';

      case 'scheduled':
        // Blue for scheduled states
        return isDark ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-600';

      case 'no_show':
        // Orange for warning/attention needed states
        return isDark ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-50 text-orange-600';

      case 'awaiting_mealplan':
        // Indigo for awaiting action states
        return isDark ? 'bg-indigo-900/20 text-indigo-400' : 'bg-indigo-50 text-indigo-600';

      case 'expired':
        // Red for error/expired states
        return isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600';

      case 'cancelled':
        // Red for cancelled/inactive states
        return isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600';

      case 'complete':
        // Green for success/completed states
        return isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-600';

      default:
        // Default gray for unknown states
        return isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(status)} ${className}`}>
      {getStatusLabel(status)}
    </span>
  );
};

export default ReferralStatusBadge;